import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL || '';

const stripExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
};

interface ClientDocument {
  id: string | number;
  file_name?: string;
  original_name?: string;
  file_size?: number;
  mime_type?: string;
  document_type?: string;
  created_at?: string;
}

const AddFilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { quoteId, clientId, returnTo } = location.state || {};
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  
  // File upload states
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // ✅ New state for uploaded documents list
  const [uploadedDocuments, setUploadedDocuments] = useState<ClientDocument[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo));
    } else if (quoteId && clientId) {
      navigate(`/quotes/master/${clientId}/${quoteId}?refreshProducts=true&productAdded=file`);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      // Auto-populate description from filename if the user hasn't typed one
      setDescription((prev) =>
        prev.trim() === '' ? stripExtension(file.name) : prev
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      // Auto-populate description from filename if the user hasn't typed one
      setDescription((prev) =>
        prev.trim() === '' ? stripExtension(file.name) : prev
      );
    }
    // Reset so selecting the same file again still fires onChange
    e.target.value = '';
  };

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setSaveMessage('Please select a file to upload');
      return;
    }
    
    setIsSubmitting(true);
    setSaveMessage('Uploading file...');
    
    try {
      const token = localStorage.getItem('token');
      
      // ✅ Handle the two cases separately:
      //   1. Quote exists → upload to /api/quotes/upload-files directly (associates with quote)
      //   2. No quote yet → keep the File object in memory; the quote-save flow will upload it

      const hasRealQuoteId =
        quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId));

      if (hasRealQuoteId) {
        // Upload directly to the quote
        const formData = new FormData();
        formData.append('files', selectedFile);
        formData.append('quoteId', String(quoteId));
        formData.append('description', description || selectedFile.name);
        if (documentType) formData.append('documentType', documentType);

        const response = await axios.post(
          `${API_URL}/api/quotes/upload-files`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const fileRecord = response.data?.documents?.[0];
        if (!fileRecord?.id) {
          throw new Error('Backend did not return a document ID');
        }

        console.log('✅ File uploaded to quote:', fileRecord.id);
        setSaveMessage('File uploaded successfully!');
      } else {
        // No quote yet — hold the File object in memory and let the save-quote flow upload it.
        // localStorage can't store File objects, so the parent page must keep it in state.
        console.log('💾 No quoteId — buffering file in parent state for upload after quote creation');

        // We rely on the browser history state to pass the actual File to CreateQuotePage
        // (sessionStorage would lose the File, so we use window state via history)
        const bufferedFile = {
          file: selectedFile,
          document_type: documentType,
          description: description || selectedFile.name,
        };

        // Store in a module-level ref that CreateQuotePage can read after navigation
        (window as any).__pendingQuoteFile = bufferedFile;

        setSaveMessage('File ready — will be attached when the quote is saved.');
      }

      // Navigate back after successful handling
      setTimeout(() => {
        handleBack();
      }, 1000);
      
    } catch (error: any) {
      console.error('Error saving file:', error);
      setSaveMessage(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Fetch existing client documents
  const fetchClientDocuments = async () => {
    if (!clientId) return;
    setIsLoadingDocs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/client-documents?clientId=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const docs = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setUploadedDocuments(docs);
    } catch (error) {
      console.error('Error fetching client documents:', error);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchClientDocuments();
  }, [clientId]);

  const handleDeleteDocument = async (docId: string | number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/client-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadedDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Upload File"
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />

        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors shadow-sm"
              >
                ← Back to Quote
              </button>
            </div>

            {/* MAIN UPLOAD CARD - EXACT MATCH TO DOCUMENTS TAB */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Upload New Document</h2>
                  <p className="text-sm text-gray-500">Add new files to client's document repository</p>
                </div>
              </div>

              {saveMessage && (
                <div className={`mb-6 p-3 rounded-lg border ${saveMessage.includes('success') || saveMessage.includes('saved') ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  {saveMessage}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Document Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Select Document Type</option>
                    <option value="id_document">ID Document</option>
                    <option value="proof_of_address">Proof of Address</option>
                    <option value="bank_statements">Bank Statements</option>
                    <option value="policy_document">Policy Document</option>
                    <option value="claim_document">Claim Document</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter document description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Drag and Drop Zone */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Select File <span className="text-red-500">*</span>
                </label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                    dragActive ? 'border-blue-500 bg-blue-50' : 'border-blue-300 bg-blue-50/50 hover:bg-blue-50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={(e) => {
                    if (e.target === fileInputRef.current) return;
                    fileInputRef.current?.click();
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  {selectedFile ? (
                    <div className="flex flex-col items-center w-full max-w-sm mx-auto">
                      {/* Mimic the Document Card layout */}
                      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 text-left relative">
                        <div className="flex justify-between items-start mb-2">
                          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          {documentType && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {documentType.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 break-all line-clamp-2">{selectedFile.name}</h4>
                        
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                            {selectedFile.name.split('.').pop()?.toUpperCase() || 'FILE'}
                          </span>
                          <span>•</span>
                          <span>{(selectedFile.size / 1024).toFixed(2)} KB</span>
                        </div>
                      </div>
                      <p className="text-sm text-blue-600 font-medium hover:underline cursor-pointer">
                        Click to change file
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-gray-500">
                      <svg className="w-12 h-12 text-blue-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <p className="text-base text-gray-700">
                        <span className="font-semibold text-blue-600 hover:text-blue-700">Upload a file</span> or drag and drop
                      </p>
                      <p className="text-sm text-gray-500 mt-1.5">
                        PDF, JPG, PNG, DOC, DOCX, TXT up to 10MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

                            {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!selectedFile || !documentType || isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {isSubmitting ? 'Uploading...' : 'Upload Document'}
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </button>
              </div>
            </div>

            {/* ✅ UPLOADED DOCUMENTS SECTION */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Uploaded Documents</h2>
                  <p className="text-sm text-gray-500">
                    {uploadedDocuments.length} document{uploadedDocuments.length !== 1 ? 's' : ''} uploaded
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={fetchClientDocuments}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh Documents
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">Click on any document to download or delete.</p>

              {isLoadingDocs ? (
                <div className="text-center py-8 text-gray-500">Loading documents...</div>
              ) : uploadedDocuments.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <p className="text-gray-500">No documents uploaded yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {uploadedDocuments.map((doc) => {
                    const fileName = doc.original_name || doc.file_name || 'Document';
                    const fileSize = doc.file_size ? (doc.file_size / 1024).toFixed(2) + ' KB' : 'Unknown';
                    const fileType = doc.mime_type?.split('/')[1]?.toUpperCase() || 'FILE';
                    const formattedDate = doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : 'Recently';

                    return (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white flex flex-col justify-between">
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            {doc.document_type && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                {doc.document_type.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-900 break-all line-clamp-2">{fileName}</h4>
                          
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                              {fileType}
                            </span>
                            <span>•</span>
                            <span>{fileSize}</span>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                          <a
                            href={`${API_URL}/api/client-documents/download/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-1 py-2 px-3 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="flex-1 flex items-center justify-center gap-1 py-2 px-3 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFilePage;